import { AxiosRequestConfig } from "axios";
import { RESPONSE_CODE } from "../constants";
import axiosInstance from "./axiosInstance";
import {
  APINewResponse,
  APIOldResponse,
  StandardResponse,
} from "./response.model";

function normalizeResponse<T>(
  res: APIOldResponse<T> | APINewResponse<T>,
): StandardResponse<T> {
  // Check for Old API response (has "code")
  if ("code" in res) {
    return {
      data: res.data,
      success: res.code === RESPONSE_CODE.success,
      statusCode: res.code,
      message: res.message,
      errors: [],
    };
  }

  // Check for New API response (has "isSuccess")
  if ("isSuccess" in res) {
    return {
      data: res.data,
      success: res.isSuccess,
      statusCode: res.statusCode,
      message: res.message,
      errors: res.errorMessages,
    };
  }
  // Fallback (should not happen if backend always returns Old or New)
  return {
    data: null as unknown as T,
    success: false,
    statusCode: RESPONSE_CODE.internalServerError,
    message: "Unknown response format",
    errors: ["Unknown response format"],
  };
}

/* ------------------ HTTP METHODS ------------------ */

const httpRequest = {
  async get<T>(url: string, config?: object): Promise<StandardResponse<T>> {
    const response = await axiosInstance.get(url, config);
    return normalizeResponse<T>(response.data);
  },

  async post<T, B>(
    url: string,
    body: B,
    config?: object,
  ): Promise<StandardResponse<T>> {
    const response = await axiosInstance.post(url, body, config);
    return normalizeResponse<T>(response.data);
  },

  async put<T, B>(
    url: string,
    body: B,
    config?: object,
  ): Promise<StandardResponse<T>> {
    const response = await axiosInstance.put(url, body, config);
    return normalizeResponse<T>(response.data);
  },

  async patch<T, B>(
    url: string,
    body: B,
    config?: object,
  ): Promise<StandardResponse<T>> {
    const response = await axiosInstance.patch(url, body, config);
    return normalizeResponse<T>(response.data);
  },

  async delete<T>(url: string, config?: object): Promise<StandardResponse<T>> {
    const response = await axiosInstance.delete(url, config);
    return normalizeResponse<T>(response.data);
  },

  async postRaw<T, B>(
    url: string,
    body: B,
    config?: AxiosRequestConfig<B>,
  ): Promise<T> {
    const response = await axiosInstance.post(url, body, config);
    return response.data;
  },
};

export default httpRequest;
